import React from 'react';

import { SvgIcon, TitleWithDescription } from '@infoods/lifebase-ui';

import classes from './EntityRow.module.scss';
import {
  GridTableCell,
  GridTableRow,
  UserInfoCell,
} from '../../../../shared/components/table-components';
import { EntityRowInfo } from '../../../interfaces/entity-list/EntityRowInfo';
import { StatusCell, type StatusCellProps } from './StatusCell/StatusCell';
import { getHumanizeEntityType } from '../../../../shared/utils/userInfoUtils';
import { getHumanizeInbodyType } from '../../../../shared/utils/inbodyUtils';
import { ReactComponent as StripeIcon } from '../../../../shared/assets/icons/stripe.svg';
import { dateFormat, localDataFormat } from '../../../../shared/constants/apiDateFormat';
import { getFormattedDate } from '../../../../shared/utils/dateUtils';
import { LIST_MODE, ListMode } from '../NgzEntityList.settings';
import { PAYMENT_FLOW_TYPE } from '../../../../shared/constants/user-info/paymentFlowType';

type EntityRowProps = {
  entityInfo: EntityRowInfo;
  listMode: ListMode;
};

const EntityRow = ({ entityInfo, listMode }: EntityRowProps) => {
  const {
    entityId,
    businessName,
    isStripeAccountConnected,
    businessEntityType,
    inbodyType,
    ownerName,
    businessPhone,
    status,
    scanIqStatus,
    subscriptionExpire,
    registrationDate,
    activeScanIqCustomerCount,
    activeNutritionCustomerCount,
    paymentFlowType,
  } = entityInfo;

  const statusConfig: Record<ListMode, StatusCellProps> = {
    [LIST_MODE.NUTRITION]: { status, subscriptionExpire },
    [LIST_MODE.SCAN_IQ]: { status: scanIqStatus, subscriptionExpire: null },
  };

  const activeCustomerConfig: Record<ListMode, number> = {
    [LIST_MODE.NUTRITION]: activeNutritionCustomerCount,
    [LIST_MODE.SCAN_IQ]: activeScanIqCustomerCount,
  };

  const entityProfilePathConfig: Record<ListMode, string> = {
    [LIST_MODE.NUTRITION]: `#!/nutrition/entity-profile/${entityId}`,
    [LIST_MODE.SCAN_IQ]: `#!/scan-iq/entity-profile/${entityId}`,
  };

  const entityProfilePath = entityProfilePathConfig[listMode];

  const displayStatusProps = statusConfig[listMode];
  const displayActiveCustomerCount =
    paymentFlowType === PAYMENT_FLOW_TYPE.NEW_USER_FLOW_2024
      ? activeCustomerConfig[listMode]
      : 0;

  return (
    <GridTableRow variant="row" dataTestId="entity-row">
      <GridTableCell variant="title" dataTestId="business-name-cell">
        <UserInfoCell
          name={businessName}
          nameAdornment={
            isStripeAccountConnected && (
              <SvgIcon
                component={StripeIcon}
                inheritViewBox
                fontSize="medium"
                data-test-id="stripe-icon"
              />
            )
          }
          extraInfo={getHumanizeEntityType(businessEntityType)}
          href={entityProfilePath}
        />
      </GridTableCell>
      <GridTableCell dataTestId="inbody-type-cell">
        {getHumanizeInbodyType(inbodyType)}
      </GridTableCell>
      <GridTableCell className={classes.ownerCell} dataTestId="owner-name-cell">
        <TitleWithDescription
          title={ownerName}
          description={businessPhone}
          titleFont="inherit"
          className="private-info"
        />
      </GridTableCell>
      <GridTableCell dataTestId="status-cell">
        <StatusCell {...displayStatusProps} />
      </GridTableCell>
      <GridTableCell dataTestId="registration-date-cell">
        {registrationDate
          ? getFormattedDate(registrationDate, localDataFormat, dateFormat)
          : '-'}
      </GridTableCell>
      <GridTableCell dataTestId="active-customer-count-cell">
        {paymentFlowType === PAYMENT_FLOW_TYPE.NEW_USER_FLOW_2024
          ? displayActiveCustomerCount || '-'
          : '-'}
      </GridTableCell>
    </GridTableRow>
  );
};

export { EntityRow };
